import { guestEnv } from '../config/env';
import type { guestApi } from '../app/api';

export type GuestGalleryData = Awaited<ReturnType<typeof guestApi.getGallery>>;
export type GuestGalleryItem = GuestGalleryData['items'][number];
export type GuestGalleryAlbum = GuestGalleryData['albums'][number];

// The gallery API returns an absolute URL for cloud storage (r2/gcs) and a
// relative `/local-assets/...` path for the local_dev provider; resolve the
// latter against the API origin so the <img> loads from the API, not the app.
export function resolveGalleryAssetUrl(assetUrl: string | null): string | null {
  if (!assetUrl) {
    return null;
  }

  return new URL(assetUrl, guestEnv.apiBaseUrl).toString();
}

// A guest-facing title for a photo: prefer the caption, fall back to the
// uploader's name. Never show the raw storage file name.
export function galleryItemLabel(item: GuestGalleryItem): string {
  return (
    item.galleryItem.caption ??
    item.galleryItem.uploaderDisplayName ??
    item.galleryItem.uploaderKind
  );
}

// Map gallery-item id -> displayable image URL, drawn from the approved feed
// and the guest's own uploads, so albums can show a cover from their contents.
export function buildAlbumCoverMap(
  items: GuestGalleryItem[],
  myUploads: GuestGalleryItem[],
): Map<string, string> {
  const covers = new Map<string, string>();

  for (const item of [...items, ...myUploads]) {
    const url = resolveGalleryAssetUrl(item.assetUrl);

    if (url && !covers.has(item.galleryItem.id)) {
      covers.set(item.galleryItem.id, url);
    }
  }

  return covers;
}

export function albumCoverUrl(
  album: GuestGalleryAlbum,
  covers: Map<string, string>,
): string | null {
  for (const entry of album.items) {
    const url = covers.get(entry.galleryItem.id);

    if (url) {
      return url;
    }
  }

  return null;
}
